import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

interface Meeting {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  meetingType: 'physical' | 'virtual' | 'hybrid';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  location?: string;
  meetingUrl?: string;
  chamaId: string;
  chamaName?: string;
  attendeeCount?: number;
  duration?: number;
}

const Meetings: React.FC = () => {
  const colors = getThemeColors('dark');

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);


  useEffect(() => {
    loadMeetings();
  }, []);

  const loadMeetings = async () => {
    try {
      setLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found');
        setMeetings([]);
        return;
      }

      const response = await fetch(API_ENDPOINTS.USER_MEETINGS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Transform backend data to match frontend interface
        const transformedMeetings: Meeting[] = result.data.map((meeting: any) => ({
          id: meeting.id,
          title: meeting.title,
          description: meeting.description || '',
          scheduledAt: meeting.scheduledAt,
          meetingType: meeting.meetingType || 'physical',
          status: meeting.status,
          location: meeting.location || '',
          meetingUrl: meeting.meetingUrl || '',
          chamaId: meeting.chamaId,
          chamaName: meeting.chamaName || '',
          attendeeCount: 0, // Backend doesn't provide this directly
          duration: meeting.duration || 60,
        }));

        setMeetings(transformedMeetings);
      } else {
        throw new Error(result.error || 'Failed to fetch meetings');
      }
    } catch (error) {
      console.error('Failed to load meetings:', error);
      // Fallback to empty array on error
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadMeetings();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getFilteredMeetings = () => {
    let filtered = meetings;

    switch (selectedFilter) {
      case 'upcoming':
        filtered = meetings.filter(m => new Date(m.scheduledAt) > new Date() && m.status !== 'cancelled');
        break;
      case 'completed':
        filtered = meetings.filter(m => m.status === 'completed');
        break;
      case 'in_progress':
        filtered = meetings.filter(m => m.status === 'in_progress');
        break;
      case 'cancelled':
        filtered = meetings.filter(m => m.status === 'cancelled');
        break;
      default:
        break;
    }

    return filtered.sort((a, b) =>
      new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
    );
  };

  const getMeetingIcon = (type: string) => {
    switch (type) {
      case 'physical':
        return '🏢';
      case 'virtual':
        return '💻';
      case 'hybrid':
        return '🔄';
      default:
        return '📅';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return colors.info;
      case 'in_progress':
        return colors.warning;
      case 'completed':
        return colors.success;
      case 'cancelled':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return '⏰';
      case 'in_progress':
        return '🔴';
      case 'completed':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '📅';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleMeetingPress = (meeting: Meeting) => {
    if (meeting.status === 'completed') {
      alert(`Navigate to Meeting Summary: /dashboard/meetings/${meeting.id}/summary`);
    } else {
      // For active/upcoming meetings, join directly based on type
      handleJoinMeeting(meeting);
    }
  };

  const handleJoinMeeting = (meeting: Meeting) => {
    if (meeting.meetingType === 'virtual') {
      alert(`Navigate to Online Meeting: /dashboard/meetings/${meeting.id}/online`);
    } else if (meeting.meetingType === 'physical') {
      alert(`Navigate to Physical Meeting: /dashboard/meetings/${meeting.id}/physical`);
    } else if (meeting.meetingType === 'hybrid') {
      // For hybrid meetings, show options
      const choice = window.confirm('Choose how to attend:\n\nOK = Join Online\nCancel = Attend Physically');
      if (choice) {
        alert(`Navigate to Online Meeting: /dashboard/meetings/${meeting.id}/online`);
      } else {
        alert(`Navigate to Physical Meeting: /dashboard/meetings/${meeting.id}/physical`);
      }
    } else {
      alert('Unknown meeting type');
    }
  };

  const filterCounts = {
    all: meetings.length,
    upcoming: meetings.filter(m => new Date(m.scheduledAt) > new Date() && m.status !== 'cancelled').length,
    completed: meetings.filter(m => m.status === 'completed').length,
    in_progress: meetings.filter(m => m.status === 'in_progress').length,
    cancelled: meetings.filter(m => m.status === 'cancelled').length,
  };

  const filters = [
    { id: 'all', name: 'All', count: filterCounts.all },
    { id: 'upcoming', name: 'Upcoming', count: filterCounts.upcoming },
    { id: 'in_progress', name: 'In Progress', count: filterCounts.in_progress },
    { id: 'completed', name: 'Completed', count: filterCounts.completed },
    { id: 'cancelled', name: 'Cancelled', count: filterCounts.cancelled },
  ];

  const filteredMeetings = getFilteredMeetings();

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
    },
    createButton: {
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
    filtersContainer: {
      display: 'flex',
      gap: spacing.xs,
      overflowX: 'auto',
      paddingBottom: spacing.xs,
    },
    filterChip: {
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.backgroundSecondary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      minHeight: 32,
      display: 'flex',
      alignItems: 'center',
    },
    filterChipSelected: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    filterText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textSecondary,
    },
    filterTextSelected: {
      color: colors.white,
    },
    meetingsList: {
      paddingBottom: spacing.xl,
    },
    meetingCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: '1px solid',
      borderColor: colors.border,
      cursor: 'pointer',
      ...getShadowStyle('sm'),
    },
    meetingHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: spacing.md,
    },
    meetingIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: 'rgba(33, 150, 243, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      flexShrink: 0,
    },
    meetingInfo: {
      flex: 1,
    },
    meetingTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    meetingMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      marginBottom: spacing.xs,
    },
    meetingDate: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    meetingType: {
      fontSize: typography.fontSize.sm,
      color: colors.primary,
      backgroundColor: colors.primary + '20',
      paddingLeft: spacing.xs,
      paddingRight: spacing.xs,
      paddingTop: 2,
      paddingBottom: 2,
      borderRadius: borderRadius.sm,
    },
    meetingLocation: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginTop: spacing.xs,
    },
    meetingDescription: {
      fontSize: typography.fontSize.base,
      color: colors.text,
      lineHeight: 1.5,
      marginTop: spacing.sm,
    },
    meetingActions: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacing.md,
      paddingTop: spacing.sm,
      borderTop: `1px solid ${colors.border}`,
    },
    meetingStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.xs,
      fontSize: typography.fontSize.sm,
    },
    joinButton: {
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
    joinButtonDisabled: {
      backgroundColor: colors.textTertiary,
      cursor: 'not-allowed',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.xxxl,
      paddingBottom: spacing.xxxl,
      minHeight: 400,
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: 28,
      fontWeight: '700',
      color: colors.text,
      marginBottom: spacing.lg,
      textAlign: 'center',
    },
    emptySubtitle: {
      fontSize: 18,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 1.5,
      maxWidth: 300,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading meetings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Meetings"
        subtitle="Manage your chama meetings and schedules"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerTop}>
              <h1 style={styles.headerTitle}>Meetings</h1>

              <div style={styles.headerActions}>
                <button
                  style={styles.createButton}
                  onClick={() => {
                    // Navigate to Create Meeting page
                    alert('Navigate to: /dashboard/meetings/create');
                    // In a real app, this would be: navigate('/dashboard/meetings/create');
                  }}
                >
                  + New Meeting
                </button>
              </div>
            </div>

            {/* Filters */}
            <div style={styles.filtersContainer}>
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  style={{
                    ...styles.filterChip,
                    ...(selectedFilter === filter.id ? styles.filterChipSelected : {}),
                  }}
                  onClick={() => setSelectedFilter(filter.id)}
                >
                  <span style={{
                    ...styles.filterText,
                    ...(selectedFilter === filter.id ? styles.filterTextSelected : {}),
                  }}>
                    {filter.name} ({filter.count})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Meetings List */}
          <div style={styles.meetingsList}>
        {filteredMeetings.length > 0 ? (
          filteredMeetings.map((meeting) => (
            <div
              key={meeting.id}
              style={styles.meetingCard}
              onClick={() => handleMeetingPress(meeting)}
            >
              <div style={styles.meetingHeader}>
                <div style={styles.meetingIcon}>
                  <span style={{ fontSize: 24 }}>
                    {getMeetingIcon(meeting.meetingType)}
                  </span>
                </div>

                <div style={styles.meetingInfo}>
                  <h3 style={styles.meetingTitle}>
                    {meeting.title}
                  </h3>

                  <div style={styles.meetingMeta}>
                    <span style={styles.meetingDate}>
                      {formatDate(meeting.scheduledAt)} at {formatTime(meeting.scheduledAt)}
                    </span>
                    <span style={styles.meetingType}>
                      {meeting.meetingType.charAt(0).toUpperCase() + meeting.meetingType.slice(1)}
                    </span>
                  </div>

                  {meeting.location && (
                    <div style={styles.meetingLocation}>
                      📍 {meeting.location}
                    </div>
                  )}

                  {meeting.description && (
                    <p style={styles.meetingDescription}>
                      {meeting.description}
                    </p>
                  )}
                </div>
              </div>

              <div style={styles.meetingActions}>
                <div style={styles.meetingStatus}>
                  <span>{getStatusIcon(meeting.status)}</span>
                  <span style={{ color: getStatusColor(meeting.status) }}>
                    {meeting.status.replace('_', ' ').toUpperCase()}
                  </span>
                  {meeting.attendeeCount && (
                    <span style={{ marginLeft: spacing.sm, color: colors.textSecondary }}>
                      👥 {meeting.attendeeCount} attendees
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: spacing.sm }}>
                  {meeting.status === 'completed' && (
                    <button
                      style={{
                        ...styles.joinButton,
                        backgroundColor: colors.secondary,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        alert(`Navigate to Meeting Summary: /dashboard/meetings/${meeting.id}/summary`);
                      }}
                    >
                      📋 View Summary
                    </button>
                  )}

                  {meeting.status !== 'completed' && meeting.status !== 'cancelled' && (
                    <button
                      style={styles.joinButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoinMeeting(meeting);
                      }}
                    >
                      {meeting.status === 'in_progress' ? 'Join Now' : 'Join Meeting'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              {selectedFilter === 'upcoming' ? '📅' :
               selectedFilter === 'completed' ? '✅' :
               selectedFilter === 'cancelled' ? '❌' : '📋'}
            </div>
            <h2 style={styles.emptyTitle}>
              {selectedFilter === 'upcoming' ? 'No upcoming meetings' :
               selectedFilter === 'completed' ? 'No completed meetings' :
               selectedFilter === 'cancelled' ? 'No cancelled meetings' :
               selectedFilter === 'in_progress' ? 'No meetings in progress' : 'No meetings found'}
            </h2>
            <p style={styles.emptySubtitle}>
              {selectedFilter === 'upcoming'
                ? 'New meetings will appear here when scheduled'
                : selectedFilter === 'completed'
                ? 'Completed meetings will be shown here'
                : 'Meetings matching your filter will appear here'
              }
            </p>
          </div>
        )}
        </div>
        </div>
      </div>

      {/* Refresh indicator */}
      {refreshing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: spacing.md,
          backgroundColor: colors.primary,
          color: colors.white,
          textAlign: 'center',
          zIndex: 1000,
        }}>
          Refreshing...
        </div>
      )}
    </div>
  );
};

export default Meetings;